import React from "react";
import { Button, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import ByzMuiTypography from "../../inputs/ByzMuiTypography";
import { alpha, useTheme } from "@mui/material/styles";

interface ByzMuiButtonProps extends React.ComponentProps<typeof Button> {
  variant?: "text" | "outlined" | "contained";
  variant2?: "text" | "outlined" | "contained";
  color?:
  | "primary"
  | "secondary"
  | "error"
  | "inherit"
  | "warning"
  | "info"
  | "success";
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  link?: string;
  differentColor?: "red" | "blue" | "light_blue";
  icon?: React.ReactNode;
  sx?: any;
  fontweight?: any;
  marginLeft?: any;
  component?: any;
  [key: string]: any;
}

const disabledStyle: React.CSSProperties = {
  backgroundColor: "#E0E0E0",
  color: "#A0A0A0",
  border: "1px solid #E0E0E0",
};

const ByzMuiButton: React.FC<ByzMuiButtonProps> = ({
  variant = "outlined",
  variant2 = "outlined",
  color = "primary",
  onClick,
  disabled = false,
  loading = false,
  children,
  link,
  differentColor,
  icon,
  sx,
  fontweight,
  marginLeft = "0px",
  ...other
}) => {
  const theme = useTheme();

  const customColors = {
    red: {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: "#fff",
      "&:hover": {
        backgroundColor: "#d32f2f",
      },
    },
    blue: {
      backgroundColor: "#1E88E5",
      border: "1px solid #2979C3",
      color: "white",
      "&:hover": {
        backgroundColor: "#1565C0",
      }
    },
    light_blue: {
      backgroundColor: "#63B4FF33",
      color: "#2979C3",
      border: "1px solid #2979C3",
      "&:hover": {
        backgroundColor: alpha("#63B4FF", 0.3),
      }
    },
    default: {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 1),
        color: "#fff",
      },
    },
  };

  const customSx =
    differentColor && differentColor in customColors
      ? customColors[differentColor]
      : customColors.default;

  const defaultSmallStyles = {
    height: "36px",
    padding: "4px 10px",
    fontSize: "13px",
    fontFamily: "Poppins",
    fontWeight: 400,
    lineHeight: "22px",
    letterSpacing: "0px",
    textTransform: "none" as const,
  };

  const finalSx = {
    ...customSx,
    ...(disabled || loading ? disabledStyle : {}),
    ...(variant2 === "outlined" && !differentColor ? {
      border: "1px solid #2979C3",
      backgroundColor: "#63B4FF33",
      color: "#2979C3",
      "&:hover": {
        backgroundColor: "#2979C3",
        color: "#fff !important",
        "& *": {
          color: "inherit !important",
        },
      },
    } : {}),
    ...defaultSmallStyles,
    ...sx,
  };

  const content = (
    <React.Fragment>
      {loading ? (
        <CircularProgress
          size={16}
          sx={{
            color: "inherit",
            marginRight: "8px"
          }}
        />
      ) : icon}
      <ByzMuiTypography color="inherit" marginLeft={marginLeft} fontWeight={fontweight || 400}>
        {children}
      </ByzMuiTypography>
    </React.Fragment>
  );

  if (link) {
    return (
      <Link to={link || ""} style={{ textDecoration: 'none' }}>
        <Button
          variant={variant2}
          color={color}
          onClick={onClick}
          disabled={disabled || loading}
          sx={finalSx}
          {...other}
        >
          {content}
        </Button>
      </Link>
    );
  }

  return (
    <Button
      variant={variant2}
      color={color}
      sx={finalSx}
      onClick={onClick}
      disabled={disabled || loading}
      {...other}
    >
      {content}
    </Button>
  );
};

export default ByzMuiButton;